import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, PlusCircle } from 'lucide-react';

function ManualRedemptionDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) return setResults([]);
    const t = setTimeout(() => {
      staffApi.get('/customers', { params: { q, pageSize: 8 } }).then(({ data }) => setResults(data.customers));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const selectCustomer = async (c) => {
    setCustomer(c);
    const { data } = await staffApi.get(`/customers/${c.id}`);
    setSubscription(data.customer.subscriptions.find((s) => s.status === 'ACTIVE') || null);
  };

  const confirm = async () => {
    if (!subscription) return;
    setBusy(true);
    try {
      await staffApi.post('/redemptions', { subscriptionId: subscription.id, quantity, method: 'MANUAL' });
      toast.success('Manual redemption recorded');
      setOpen(false);
      setCustomer(null);
      setSubscription(null);
      setQ('');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="manual-redemption-open-btn" className="bg-saffron hover:bg-saffron-hover">
          <PlusCircle size={16} className="mr-2" /> Manual Redemption
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="manual-redemption-dialog">
        <DialogHeader>
          <DialogTitle>Manual Redemption</DialogTitle>
        </DialogHeader>

        {!customer && (
          <div className="space-y-2">
            <Input data-testid="manual-redemption-search" placeholder="Search customer…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {results.map((c) => (
                <button
                  key={c.id}
                  data-testid="manual-redemption-customer-option"
                  onClick={() => selectCustomer(c)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-bone text-sm"
                >
                  {c.name} · {c.phone}
                </button>
              ))}
            </div>
          </div>
        )}

        {customer && (
          <div className="space-y-3">
            <p className="text-sm">
              <span className="font-medium">{customer.name}</span> · {customer.phone}
            </p>
            {subscription ? (
              <>
                <p className="text-sm text-muted-ink">
                  {subscription.plan?.name} — {subscription.remainingCredits} remaining
                </p>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">Quantity</p>
                  <Input
                    type="number"
                    min={1}
                    data-testid="manual-redemption-quantity-input"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-destructive">This customer has no active subscription.</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            data-testid="manual-redemption-confirm-btn"
            disabled={!subscription || busy}
            onClick={confirm}
            className="bg-saffron hover:bg-saffron-hover"
          >
            {busy ? 'Recording…' : 'Confirm Redemption'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Redemptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = searchParams.get('range') || '';
  const [q, setQ] = useState('');
  const [redemptions, setRedemptions] = useState([]);
  const [total, setTotal] = useState(0);

  const load = () => {
    const params = { q };
    if (range) params.range = range;
    return staffApi.get('/redemptions', { params }).then(({ data }) => {
      setRedemptions(data.redemptions);
      setTotal(data.total);
    });
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, range]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Redemptions</h1>
        <ManualRedemptionDialog onDone={load} />
      </div>

      {range === 'today' && (
        <div className="flex items-center gap-2 text-sm text-muted-ink">
          <span>
            Showing <strong className="text-charcoal">today's redemptions</strong>
          </span>
          <button
            type="button"
            data-testid="redemptions-filter-clear"
            onClick={() => setSearchParams({})}
            className="text-saffron underline"
          >
            Clear
          </button>
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
        <Input data-testid="redemptions-search-input" placeholder="Search by customer or phone…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Date/Time</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Staff</TableHead>
              <TableHead>Method</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {redemptions.map((r) => (
              <TableRow key={r.id} data-testid="redemption-list-row">
                <TableCell>{new Date(r.createdAt).toLocaleString('en-CA')}</TableCell>
                <TableCell>{r.subscription?.customer?.name}</TableCell>
                <TableCell>{r.subscription?.plan?.name}</TableCell>
                <TableCell>-{r.quantity}</TableCell>
                <TableCell>{r.balanceBefore} → {r.balanceAfter}</TableCell>
                <TableCell>{r.staff?.name}</TableCell>
                <TableCell>{r.method}</TableCell>
              </TableRow>
            ))}
            {redemptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-ink py-8">No redemptions found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-ink">{total} result(s)</p>
    </div>
  );
}
